import { useCallback, useEffect, useRef, useState } from 'react'
import { api, RequestFailed, type AssetRecord, type Role } from '../api'
import { Icon } from './Icon'

/**
 * The asset library: every file uploaded to this project, in one grid.
 *
 * The thumbnails are DERIVED copies, 320 pixels wide, served from
 * `/assets/<key>.thumb.webp` — the original's key plus the transform, under
 * the original's own visibility rule. They used to be the originals
 * themselves, drawn into a 156-pixel cell, so opening this screen downloaded
 * every full-size screenshot the project had ever taken to paint a grid of
 * postage stamps. An image with no thumbnail — an SVG, a PDF, a GIF, a file
 * whose derive failed — falls back to the original, which is what every file
 * did before: a missing thumbnail costs bandwidth, never a picture.
 *
 * The two decisions that matter both protect against a silent loss:
 * usage is read on demand rather than shown for free, because it means
 * reading every open draft as well as the published tree and is not cheap for
 * a library nobody is about to delete from; and a delete that would break a
 * page is refused, with the pages named, rather than quietly warned about
 * after the fact.
 */
const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })

interface UsageState {
  loading: boolean
  pages: string[] | null
}

export function LibraryScreen({
  you,
  branch,
  onAddImage,
}: {
  you: { role: Role; canPublish: boolean } | null
  /** The writable draft replacements are staged on; null hides Replace. */
  branch: string | null
  /**
   * The one act an empty library can offer: go to a page and put an image on
   * it. Deliberately not an upload button here — a file that no page uses is
   * not an asset, it is a stray, and this screen's whole claim is that it
   * shows where each image is USED.
   */
  onAddImage: () => void
}) {
  const [assets, setAssets] = useState<AssetRecord[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [altDrafts, setAltDrafts] = useState<Record<string, string>>({})
  const [saved, setSaved] = useState<string | null>(null)
  const [usage, setUsage] = useState<Record<string, UsageState>>({})
  const [refusals, setRefusals] = useState<Record<string, string[]>>({})
  const [note, setNote] = useState<string | null>(null)
  const replacePicker = useRef<HTMLInputElement | null>(null)
  const replacing = useRef<AssetRecord | null>(null)

  /**
   * Replace: a new file takes over every reference on the CURRENT draft, as
   * staged edits a person can review and discard. The old file stays — other
   * drafts may use it, and deleting has its own button and its own guard.
   */
  const doReplace = async (file: File) => {
    const target = replacing.current
    if (!target || !branch) return
    setBusy(target.key)
    setNote(null)
    try {
      const result = await api.replaceAsset(target.key, file, branch)
      setNote(
        result.pages.length === 0
          ? 'Stored, but nothing on this draft used the old file — the pages are unchanged.'
          : `Replaced on ${result.pages.length} page${result.pages.length === 1 ? '' : 's'}, staged on ${branch.replace(/^draft\//, '')} for review.`,
      )
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
      replacing.current = null
    }
  }

  const load = useCallback(async () => {
    try {
      const { assets: list } = await api.assets()
      setAssets(list)
      // Only fill in a draft for a file that has not been typed into yet —
      // overwriting an in-flight edit on every reload would lose keystrokes.
      setAltDrafts((prev) => {
        const next = { ...prev }
        for (const a of list) if (!(a.key in next)) next[a.key] = a.altText ?? ''
        return next
      })
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [])

  useEffect(() => void load(), [load])

  const mayWrite = you?.role === 'owner' || you?.role === 'admin' || you?.role === 'editor'

  const saveAlt = async (asset: AssetRecord) => {
    const value = (altDrafts[asset.key] ?? '').trim()
    if (value === (asset.altText ?? '')) return
    setBusy(asset.key)
    try {
      await api.setAssetAlt(asset.key, value)
      setAssets(
        (prev) =>
          prev?.map((a) => (a.key === asset.key ? { ...a, altText: value || null } : a)) ?? prev,
      )
      setSaved(asset.key)
      window.setTimeout(() => setSaved((s) => (s === asset.key ? null : s)), 1500)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy((b) => (b === asset.key ? null : b))
    }
  }

  const toggleUsage = async (key: string) => {
    if (usage[key]) {
      setUsage((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
      return
    }
    setUsage((prev) => ({ ...prev, [key]: { loading: true, pages: null } }))
    try {
      const result = await api.assetUsage(key)
      setUsage((prev) =>
        prev[key] ? { ...prev, [key]: { loading: false, pages: result.pages } } : prev,
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setUsage((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
    }
  }

  const doDelete = async (asset: AssetRecord, force = false) => {
    setBusy(asset.key)
    setError(null)
    try {
      await api.deleteAsset(asset.key, force)
      setAssets((prev) => prev?.filter((a) => a.key !== asset.key) ?? prev)
      setRefusals((prev) => {
        const next = { ...prev }
        delete next[asset.key]
        return next
      })
    } catch (e) {
      if (e instanceof RequestFailed && e.code === 'in_use') {
        const pages = Array.isArray(e.body['pages']) ? (e.body['pages'] as string[]) : []
        setRefusals((prev) => ({ ...prev, [asset.key]: pages }))
      } else {
        setError(e instanceof Error ? e.message : String(e))
      }
    } finally {
      setBusy((b) => (b === asset.key ? null : b))
    }
  }

  if (!assets) {
    return (
      <div className="screen">
        <header className="screen__head">
          <h1>Library</h1>
        </header>
        {error ? (
          <p className="screen__error">
            <Icon name="alert" size={13} /> {error}
          </p>
        ) : (
          <div className="screen__loading" aria-busy="true">
            <p className="sr-only">Loading…</p>
            <span className="skeleton" />
            <span className="skeleton" />
            <span className="skeleton" />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="screen">
      <header className="screen__head">
        <h1>Library</h1>
        <p className="screen__sub">
          {/* The empty state below already explains where files come from, in
              the same words. Saying it twice on one screen reads as a stutter,
              so the header carries what the place IS while it is empty. */}
          {assets.length === 0 ? (
            'Every file your pages use, with where each one appears and what it says.'
          ) : (
            <>
              {assets.length} file{assets.length === 1 ? '' : 's'}. A file lands here the moment it
              is added to a page — there is nothing to upload from this screen.
            </>
          )}
        </p>
      </header>

      <input
        ref={replacePicker}
        type="file"
        accept="image/*,.pdf,.zip"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0]
          e.target.value = ''
          if (f) void doReplace(f)
        }}
      />
      {note && <p className="home__quiet">{note}</p>}
      {error && (
        <p className="screen__error">
          <Icon name="alert" size={13} /> {error}
        </p>
      )}

      {assets.length === 0 ? (
        <div className="empty">
          <span className="empty__icon">
            <Icon name="image" size={20} />
          </span>
          <span className="empty__title">No files yet</span>
          <span className="empty__text">
            A file lands here the moment you put one on a page — an image, a diagram, a PDF. The
            library then tracks where each one is used, and what an image’s alt text says.
            {you?.role === 'viewer' && ' Adding one is a writer’s job.'}
          </span>
          {/* Not offered to a viewer: they may read every page and change
              none, so this button could only walk them into a refusal. */}
          {you?.role !== 'viewer' && (
            <button className="btn btn--tinted btn--sm" onClick={onAddImage}>
              <Icon name="page" size={13} />
              Add a file to a page
            </button>
          )}
        </div>
      ) : (
        <div className="library__grid">
          {assets.map((asset) => {
            const isImage = asset.contentType.startsWith('image/')
            const usageState = usage[asset.key]
            const refusal = refusals[asset.key]
            return (
              <div className="library__card" key={asset.id}>
                <div className="library__thumb">
                  {isImage ? (
                    <img
                      src={asset.thumbUrl ?? asset.url}
                      alt={asset.altText ?? ''}
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <Icon name="page" size={22} />
                  )}
                </div>

                {/* Not truncated any more. The file's name is what a person
                    picks it out by, and it was cut off at one line with the
                    rest of it in a `title` — reachable by hovering a `<div>`,
                    which is to say by a mouse and by nothing else. A card has
                    vertical room where a row would not, so the name simply
                    wraps and everybody reads all of it. */}
                <div className="library__name">{asset.originalName ?? asset.key}</div>
                <div className="library__meta">
                  {formatBytes(asset.bytes)}
                  {asset.width && asset.height ? ` · ${asset.width}×${asset.height}` : ''}
                  {' · '}
                  {formatDate(asset.createdAt)}
                </div>

                <label className="library__field">
                  <span>Alt text</span>
                  <input
                    className="library__alt"
                    value={altDrafts[asset.key] ?? ''}
                    placeholder="Describe it for a screen reader"
                    disabled={!mayWrite || busy === asset.key}
                    onChange={(e) =>
                      setAltDrafts((prev) => ({ ...prev, [asset.key]: e.target.value }))
                    }
                    onBlur={() => void saveAlt(asset)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                    }}
                  />
                </label>
                {saved === asset.key && (
                  <span className="library__saved">
                    <Icon name="check" size={11} /> Saved
                  </span>
                )}

                <div className="library__row">
                  <button
                    className="btn btn--ghost btn--sm"
                    onClick={() => void toggleUsage(asset.key)}
                  >
                    <Icon name="eye" size={12} />
                    {usageState ? 'Hide usage' : 'Usage'}
                  </button>
                  {mayWrite && branch && (
                    <button
                      className="btn btn--ghost btn--sm"
                      disabled={busy === asset.key}
                      data-tip="A new file takes over every use on the current draft; the old one stays."
                      onClick={() => {
                        replacing.current = asset
                        replacePicker.current?.click()
                      }}
                    >
                      <Icon name="redo" size={12} />
                      Replace
                    </button>
                  )}
                  {mayWrite && (
                    <button
                      className="btn btn--ghost btn--sm"
                      disabled={busy === asset.key}
                      onClick={() => void doDelete(asset)}
                    >
                      {busy === asset.key ? (
                        <Icon name="spinner" size={12} />
                      ) : (
                        <Icon name="trash" size={12} />
                      )}
                      Delete
                    </button>
                  )}
                </div>

                {usageState && (
                  <div className="library__usage">
                    {usageState.loading ? (
                      <span>
                        <Icon name="spinner" size={12} /> Checking…
                      </span>
                    ) : usageState.pages && usageState.pages.length > 0 ? (
                      <>
                        <span>
                          Used on {usageState.pages.length} page
                          {usageState.pages.length === 1 ? '' : 's'}:
                        </span>
                        <ul>
                          {usageState.pages.map((p) => (
                            <li key={p}>
                              <code>{p}</code>
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <span>Not used on any page.</span>
                    )}
                  </div>
                )}

                {refusal && (
                  <div className="library__refusal">
                    <span>
                      <Icon name="alert" size={12} /> Used on {refusal.length} page
                      {refusal.length === 1 ? '' : 's'} — deleting would leave a broken link on each
                      of them.
                    </span>
                    <ul>
                      {refusal.slice(0, 6).map((p) => (
                        <li key={p}>
                          <code>{p}</code>
                        </li>
                      ))}
                      {refusal.length > 6 && <li>…and {refusal.length - 6} more</li>}
                    </ul>
                    <div className="library__row">
                      {you?.role === 'owner' || you?.role === 'admin' ? (
                        <button
                          className="btn btn--danger btn--sm"
                          disabled={busy === asset.key}
                          onClick={() => void doDelete(asset, true)}
                        >
                          Delete anyway
                        </button>
                      ) : (
                        <span className="home__quiet">
                          Breaking those pages on purpose is an owner or admin call.
                        </span>
                      )}
                      <button
                        className="btn btn--ghost btn--sm"
                        onClick={() =>
                          setRefusals((prev) => {
                            const next = { ...prev }
                            delete next[asset.key]
                            return next
                          })
                        }
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
